import { XMarkIcon } from "@heroicons/react/24/outline";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ComparisonDimension } from "#shared/comparisonDimension.ts";
import { type ComparisonBreakdown, toGroupSeriesId } from "#shared/comparisonSelection.ts";
import { Button } from "@/components/ui/button.tsx";
import { SeriesRowBadge } from "../SeriesRowBadge.tsx";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { BreakdownButton } from "./BreakdownButton.tsx";
import type { ComparisonSeries } from "./useComparisonSeries.ts";

const NESTED_ROW_CLASSNAME = "ps-9";

export interface BreakdownGroupRowsProps {
    nodeId: string;
    breakdown: ComparisonBreakdown;
    seriesById: Map<string, ComparisonSeries>;
    maxSelections: number;
    usedSeriesCount: number;
    maxSeriesCount: number;
    onBreakdownChange: (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) => void;
}

interface BreakdownGroupRowProps {
    nodeId: string;
    dimension: ComparisonDimension;
    groupKey: string;
    series: ComparisonSeries | undefined;
    groupKeys: string[];
    onBreakdownChange: (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) => void;
}

function BreakdownGroupRow({ nodeId, dimension, groupKey, series, groupKeys, onBreakdownChange }: BreakdownGroupRowProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("BreakdownGroupRows", { i18n: i18nextInstance });

    const handleRemove = useCallback(
        () =>
            onBreakdownChange(
                nodeId,
                dimension,
                groupKeys.filter(key => key !== groupKey)
            ),
        [onBreakdownChange, nodeId, dimension, groupKeys, groupKey]
    );

    const label = series?.label ?? groupKey;

    return (
        <div className={`flex items-center gap-2 ${NESTED_ROW_CLASSNAME}`}>
            <SeriesRowBadge letter={series?.letter ?? ""} swatchClassName={series?.swatchClassName ?? ""} size="sm" />

            <span className="sr-only">{t("seriesLabel", { letter: series?.letter ?? "", label })}</span>

            {/* intent-ui-deviation: a static chip — Badge takes its own `intent` palette and padding, and this has to
                match the shell of the filter chips it stacks under while staying non-interactive. */}
            <span className="border-border bg-card text-muted-fg flex h-7 min-w-0 items-center rounded-lg border px-2.5 text-xs font-medium">
                <span className="truncate">{label}</span>
            </span>

            <Button intent="plain" size="sq-sm" className="ms-auto size-7" aria-label={t("remove", { label })} onPress={handleRemove}>
                <XMarkIcon className="size-3.5" aria-hidden="true" />
            </Button>
        </div>
    );
}

export function BreakdownGroupRows({ nodeId, breakdown, seriesById, maxSelections, usedSeriesCount, maxSeriesCount, onBreakdownChange }: BreakdownGroupRowsProps) {
    return (
        <>
            {breakdown.groupKeys.map(groupKey => (
                <BreakdownGroupRow key={groupKey} nodeId={nodeId} dimension={breakdown.dimension} groupKey={groupKey} groupKeys={breakdown.groupKeys} series={seriesById.get(toGroupSeriesId(nodeId, breakdown.dimension, groupKey))} onBreakdownChange={onBreakdownChange} />
            ))}

            <div className={NESTED_ROW_CLASSNAME}>
                <BreakdownButton nodeId={nodeId} dimension={breakdown.dimension} breakdown={breakdown} maxSelections={maxSelections} usedSeriesCount={usedSeriesCount} maxSeriesCount={maxSeriesCount} size="sm" onBreakdownChange={onBreakdownChange} />
            </div>
        </>
    );
}
